"use client";

import { useEffect, useRef, useState } from "react";
import { Send, X, Smile, CornerUpLeft, CloudRain } from "lucide-react";

import { useChat, CHAT_MAX_LENGTH } from "@/app/hooks/useChat";
import Avatar from "@/app/components/Avatar";
import RainBanner from "@/app/components/Chat/RainBanner";
import SokoAmount from "@/app/components/SokoAmount";

const EMOJIS = [
    "😀","😂","😍","🔥","👋","👍","👎","😢","😮","🎉",
    "💰","🚀","💎","🤑","😎","🙏","❤️","💀","🤣","😅",
    "😤","🥳","😭","🫡","👀","💯","🫶","⚡","🍀","🎰",
];

interface ReplyTo {
    id: number;
    username: string;
    message: string;
}


export default function CasinoChat({ className, hideHeader }: { className?: string; hideHeader?: boolean }) {
    const { messages, sending, error, loggedIn, canModerate, currentUserId, send, remove } = useChat();
    const [draft, setDraft] = useState("");
    const [showEmoji, setShowEmoji] = useState(false);
    const [replyTo, setReplyTo] = useState<ReplyTo | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const emojiRef = useRef<HTMLDivElement>(null);
    const stickToBottom = useRef(true);

    useEffect(() => {
        const el = listRef.current;
        if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
    }, [messages]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
                setShowEmoji(false);
            }
        };
        if (showEmoji) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showEmoji]);

    const onScroll = () => {
        const el = listRef.current;
        if (!el) return;
        stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    };

    const insertEmoji = (emoji: string) => {
        const input = inputRef.current;
        if (!input) return;
        const start = input.selectionStart ?? draft.length;
        const end = input.selectionEnd ?? draft.length;
        const next = (draft.slice(0, start) + emoji + draft.slice(end)).slice(0, CHAT_MAX_LENGTH);
        setDraft(next);
        setShowEmoji(false);
        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(start + emoji.length, start + emoji.length);
        });
    };

    const handleReply = (id: number, username: string, message: string) => {
        setReplyTo({ id, username, message });
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const cancelReply = () => {
        setReplyTo(null);
    };

    const submit = async () => {
        if (!draft.trim()) return;
        const reply = replyTo
            ? { reply_to_id: replyTo.id, reply_to_username: replyTo.username, reply_to_snippet: replyTo.message.slice(0, 100) }
            : undefined;
        const ok = await send(draft, reply);
        if (ok) {
            setDraft("");
            setReplyTo(null);
            stickToBottom.current = true;
        }
    };

    return (
        <div className={className ?? "flex flex-col h-[28rem] bg-gray-900 rounded-xl overflow-hidden shadow-xl"}>
            {!hideHeader && (
                <div className="flex items-center gap-2 px-4 py-3">
                    <span className="font-semibold text-white text-base">Chat</span>
                </div>
            )}

            {/* Rain — countdown / JOIN window / payout. Renders nothing when the
                feature flag is off. Sits above the feed so it never scrolls away. */}
            <RainBanner />

            {/* Messages */}
            <div
                ref={listRef}
                onScroll={onScroll}
                className="flex-1 overflow-y-auto px-3 py-3 space-y-3.5 min-h-0 bg-gray-900"
            >
                {!loggedIn && (
                    <p className="text-center text-xs font-mono text-gray-600 mt-8">
                        Log in with Kick to view the chat
                    </p>
                )}
                {loggedIn && messages.length === 0 && (
                    <p className="text-center text-xs font-mono text-gray-600 mt-8">
                        No messages yet — say hi 👋
                    </p>
                )}
                {loggedIn && messages.map((m) => {
                    // Rain tips are real chat messages, just rendered as an
                    // announcement line instead of someone talking.
                    if (m.kind === "rain_tip") return (
                    <div key={m.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-800/60">
                        <CloudRain className="w-3.5 h-3.5 text-green-400 shrink-0" />
                        <span className="text-xs text-gray-400 min-w-0 truncate">
                            <span className="font-semibold text-gray-200">{m.username}</span> {m.message}
                        </span>
                        <span className="ml-auto text-xs font-semibold text-green-400 shrink-0">
                            <SokoAmount value={m.meta?.amount ?? 0} size={12} />
                        </span>
                    </div>
                    );

                    return (
                    <div key={m.id} className="group flex items-start gap-2.5">
                        <Avatar
                            src={m.avatar}
                            username={m.username}
                            className="w-8 h-8 rounded-full shrink-0 mt-0.5"
                            fallbackClassName="bg-gray-700 text-xs"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                                <span
                                    className={`text-sm font-semibold ${
                                        m.user_id === currentUserId ? "text-white" : "text-gray-300"
                                    }`}
                                >
                                    {m.username}
                                </span>
                                <span className="text-xs text-gray-600">
                                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>

                            {/* Reply quote block */}
                            {m.reply_to_username && m.reply_to_snippet && (
                                <div className="mt-0.5 mb-1 flex items-center gap-1.5 text-[11px] text-gray-500">
                                    <CornerUpLeft className="w-3 h-3 shrink-0 text-gray-600" />
                                    <span className="font-semibold text-gray-400 shrink-0">@{m.reply_to_username}</span>
                                    <span className="truncate">{m.reply_to_snippet}</span>
                                </div>
                            )}

                            <p className="text-gray-200 text-[15px] break-words leading-snug mt-0.5">{m.message}</p>
                        </div>

                        {/* Action buttons on hover */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 shrink-0 mt-1">
                            {loggedIn && (
                                <button
                                    onClick={() => handleReply(m.id, m.username, m.message)}
                                    title="Reply"
                                    className="text-gray-600 hover:text-emerald-400 transition-colors duration-200"
                                >
                                    <CornerUpLeft className="w-3.5 h-3.5" />
                                </button>
                            )}
                            {canModerate && (
                                <button
                                    onClick={() => remove(m.id)}
                                    title="Delete message"
                                    className="text-gray-600 hover:text-red-400 transition-colors duration-200"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Composer */}
            <div className="border-t border-gray-800 p-3 bg-gray-900">
                {error && <p className="text-xs font-mono text-red-400 mb-2">{error}</p>}

                {/* Reply banner */}
                {replyTo && (
                    <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-gray-800">
                        <CornerUpLeft className="w-3 h-3 text-emerald-400 shrink-0" />
                        <span className="text-xs text-emerald-400 font-semibold shrink-0">Replying to @{replyTo.username}</span>
                        <span className="text-xs text-gray-500 truncate">{replyTo.message}</span>
                        <button onClick={cancelReply} className="ml-auto text-gray-600 hover:text-gray-300 transition-colors duration-200 shrink-0">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* Emoji picker */}
                    <div ref={emojiRef} className="relative">
                        <button
                            onClick={() => setShowEmoji(p => !p)}
                            disabled={!loggedIn}
                            className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-yellow-300 hover:bg-gray-800 transition-all duration-200 disabled:opacity-40"
                            title="Emoji"
                        >
                            <Smile className="w-5 h-5" />
                        </button>
                        {showEmoji && (
                            <div className="absolute bottom-full left-0 mb-2 z-10 bg-gray-800 border border-gray-700 rounded-xl p-2 shadow-xl grid grid-cols-6 gap-1 w-52">
                                {EMOJIS.map((e) => (
                                    <button
                                        key={e}
                                        onClick={() => insertEmoji(e)}
                                        className="w-8 h-8 flex items-center justify-center text-lg rounded-lg hover:bg-gray-700 transition-colors duration-200"
                                    >
                                        {e}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <input
                        ref={inputRef}
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value.slice(0, CHAT_MAX_LENGTH))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                            if (e.key === "Escape" && replyTo) cancelReply();
                        }}
                        disabled={!loggedIn || sending}
                        placeholder={loggedIn ? "Type a message…" : "Log in to chat"}
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-gray-200 placeholder:text-gray-500 focus:outline-none focus:border-gray-500 transition-all duration-200 disabled:opacity-50"
                    />
                    <button
                        onClick={submit}
                        disabled={!loggedIn || sending || !draft.trim()}
                        className="p-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Send"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
